/**
 * Everything on the WhatsApp tab that does not decide whether a message can be
 * sent today — collapsed, and one press away.
 *
 * ## The other side of the line
 *
 * `WhatsAppSendState` is the fold and states the rule: **does this affect whether
 * you can send?** What is in here answers no, and each one was argued rather than
 * swept:
 *
 *   - **the ids the connection was made with.** A WABA id and a phone number id
 *     are what you check when reconciling two accounts, roughly once, and they
 *     are the last thing anybody needs when messages have stopped;
 *   - **when the connection was last checked.** The summary strip above already
 *     dates the figures it shows, which is the version of this that is
 *     load-bearing;
 *   - **how Meta meters conversations.** It is the thing everyone gets wrong when
 *     *planning* a campaign, which is a different day from the day they open this
 *     tab. It moved behind the disclosure and did **not** get shorter on the way:
 *     "per message" is the assumption everybody arrives with, and half a
 *     correction is worse than none;
 *   - **disconnecting**, which is destructive, deliberate and rare.
 *
 * The old card gave all four the same weight as the token warning, and the
 * disconnect block was the largest thing on a healthy connection. That is the
 * accretion this slice removes: not a paragraph too long, a hierarchy that never
 * existed.
 *
 * **Account review status is not in here**, and that was the one that had to be
 * argued the other way. `REJECTED` or `DISABLED` from Meta is a hard stop and
 * `metaReadiness()` folds only `pending` into a readiness state, so a rejected
 * account would have had no other route to the screen. It stays on the summary
 * strip above the fold, with Meta's own word in it.
 *
 * ## The disclosure itself is `AdvancedDisclosure`, and was not always
 *
 * **Superseded, and the superseded position is worth keeping.** This file first
 * hand-rolled its own `<details>`, because the email half of slice 16 was being
 * built in parallel and reaching into it would have meant a merge conflict on a
 * file two people were writing. `AdvancedDisclosure`'s own header names that
 * duplication as a hole and says it closes once both halves have landed — they
 * have, so it is closed here. Two disclosures that looked identical and could
 * drift apart on one page is the exact accretion this slice exists to undo, and
 * leaving them because both worked is how the page got the way it was.
 *
 * The argument for `<details>` over a `<button aria-expanded>` is stated in full
 * there and is not repeated. The half that matters most for *this* tab: somebody
 * handed a WABA id in a Meta support thread, wanting to know whether it is the
 * one connected, will press Ctrl+F — and Chrome and Safari open a closed
 * `<details>` to reveal a match, while unmounted JSX is simply not on the page.
 *
 * ## No request lives here
 *
 * `disconnect` is a `ReactNode` slot rather than an imported panel. The API
 * client reaches `lib/config.ts` and `import.meta.env`, which evaluates only
 * inside Vite, so importing it would put this file out of reach of
 * `verify-whatsapp-tab.tsx` — and what is worth asserting about a disclosure is
 * exactly which values are inside it.
 */
import { Kicker } from '@wend/ui';
import type { ReactNode } from 'react';

import type { WhatsAppIntegration } from '../../lib/api/whatsapp';
import { formatDateTime } from '../../lib/format';
import { WA_CONVERSATION_NOTE } from '../../lib/whatsapp-display';
import { AdvancedDisclosure } from './AdvancedDisclosure';
import type { WhatsAppReading } from './whatsapp-reading';

export function WhatsAppAdvanced({
  integration,
  reading,
  disconnect,
}: {
  integration: WhatsAppIntegration;
  reading: WhatsAppReading;
  /** The disconnect control. See the header: this file makes no request. */
  disconnect: ReactNode;
}) {
  return (
    <AdvancedDisclosure summary="Account ids, how Meta bills, disconnecting">
      <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
        {/*
          The row's own id is the fallback for the WABA name, exactly as the
          summary strip falls back: the name comes from the last successful
          check, and a first paint should still name the account rather than
          print an em dash.
        */}
        <Detail label="WABA">{reading.check?.waba.name ?? integration.wabaId}</Detail>
        <Detail label="WABA id">{integration.wabaId}</Detail>
        <Detail label="Phone number id">{integration.phoneNumberId}</Detail>
        <Detail label="Last checked">
          {integration.lastVerifiedAt === null
            ? 'Never'
            : formatDateTime(integration.lastVerifiedAt)}
        </Detail>
      </dl>

      <p className="max-w-prose text-[0.9rem] leading-relaxed text-muted">{WA_CONVERSATION_NOTE}</p>

      {disconnect}
    </AdvancedDisclosure>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <Kicker as="dt">{label}</Kicker>
      <dd className="mt-1 break-words text-[0.92rem] text-text">{children}</dd>
    </div>
  );
}
